/** Save / Load template popups for the Распоред scheduler. SaveTemplateModal
 *  names the current period's shifts as a reusable template; LoadTemplateModal
 *  lists templates for the current scope and applies (or deletes) one. */
import type { ShiftTemplate } from '@gym/api-client';
import { FolderOpen, Trash2 } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, IconButton } from '../../../components/ui/Button.js';
import { EmptyState, Spinner } from '../../../components/ui/Feedback.js';
import { Field, Input } from '../../../components/ui/Field.js';
import { Modal } from '../../../components/ui/Modal.js';
import { toast } from '../../../components/ui/Toaster.js';

import type { ScheduleView } from './scheduleUtils.js';
import { useDeleteTemplate, useShiftTemplates } from './useShiftTemplates.js';

export function SaveTemplateModal({
  open,
  onOpenChange,
  count,
  saving,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  count: number;
  saving: boolean;
  onSubmit: (name: string) => void;
}): ReactNode {
  const { t } = useTranslation();
  const [name, setName] = useState('');

  useEffect(() => {
    if (open) setName('');
  }, [open]);

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!saving) onOpenChange(o);
      }}
      title={t('staff.schedule.save_template')}
      size="sm"
      footer={
        <>
          <Button variant="secondary" disabled={saving} onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            loading={saving}
            disabled={!name.trim()}
            onClick={() => onSubmit(name.trim())}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="grid gap-3">
        <Field label={t('staff.schedule.template_name')} required>
          {(id) => (
            <Input
              id={id}
              aria-label="templateName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          )}
        </Field>
        <p className="text-sm text-muted">{t('staff.schedule.save_template_hint', { count })}</p>
      </div>
    </Modal>
  );
}

export function LoadTemplateModal({
  open,
  onOpenChange,
  scope,
  loading,
  onLoad,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scope: ScheduleView;
  loading: boolean;
  onLoad: (template: ShiftTemplate) => void;
}): ReactNode {
  const { t } = useTranslation();
  const templates = useShiftTemplates(scope);
  const del = useDeleteTemplate();
  const rows = templates.data ?? [];

  function onDelete(id: string): void {
    del.mutate(id, {
      onError: (e) => toast.error(t('common.error'), e instanceof Error ? e.message : undefined),
    });
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t('staff.schedule.load_template')}
      footer={
        <Button variant="secondary" onClick={() => onOpenChange(false)}>
          {t('common.cancel')}
        </Button>
      }
    >
      {templates.isLoading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <EmptyState icon={FolderOpen} title={t('staff.schedule.no_templates')} />
      ) : (
        <div className="grid gap-2">
          {rows.map((tpl) => (
            <div
              key={tpl.id}
              className="flex items-center gap-3 rounded-lg border border-border bg-surface px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-fg">{tpl.name}</div>
                <div className="text-xs text-muted">
                  {t('staff.schedule.template_shifts', { count: tpl.entries.length })}
                </div>
              </div>
              <Button variant="primary" size="sm" loading={loading} onClick={() => onLoad(tpl)}>
                {t('staff.schedule.load')}
              </Button>
              <IconButton
                label={t('staff.schedule.delete_template')}
                variant="ghost"
                disabled={del.isPending}
                onClick={() => onDelete(tpl.id)}
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </IconButton>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
